import React, { ReactElement } from 'react';
import { get } from 'lodash';
import { FieldError as IFieldError, useFormContext } from 'react-hook-form';
import { Flex, Tooltip } from '@chakra-ui/react';
import { ButtonIcon, FormFeedback, Input } from '@morressier/alembic';
import { FieldLabel } from '../Field';
import { IFormFieldText } from '../Submission/GenericFieldComponents/Text';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';
import { handleWhiteSpaceValidation } from '../UserRegistrationSteps/StepThree/signupHook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
interface IFormFieldTextProps {
  config: IFormFieldText;
  placeholder?: string;
  isEditingAllowed?: boolean;
  fieldName?: string;
  additionalElement?: ReactElement;
  isShowHelpInfo?: boolean;
}

const CustomFormFieldTextInput: React.FC<IFormFieldTextProps> = ({
  config,
  placeholder,
  isEditingAllowed = true,
  fieldName,
  additionalElement,
  isShowHelpInfo
}) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue
  } = useFormContext();

  const {
    name,
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const formField = fieldName ? fieldName : field;

  const error = get(errors, formField) as IFieldError;
  const value = watch(formField) as string;
  const isEmailField = labels.main.toLowerCase().includes('email');
  const emailMaxLength = isEmailField
    ? 128
    : 'maxLength' in validations
    ? validations.maxLength
    : undefined;

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <Flex
        gap={3}
        alignItems={'center'}
        justifyContent={'space-between'}
        width="full"
      >
        <Flex alignItems={'center'}>
          <FieldLabel
            label={labels.main}
            subText={!isShowHelpInfo && labels.subtitle}
            isRequired={validations.required}
            helpText={labels.helpText}
          />
          {isShowHelpInfo && (
            <Tooltip
              hasArrow
              shouldWrapChildren
              placement="top"
              label={labels.subtitle}
              borderRadius={4}
              data-cy="info-tooltip"
            >
              <ButtonIcon
                size="small"
                ariaLabel={labels.main}
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                data-cy="info-icon"
              />
            </Tooltip>
          )}
        </Flex>
        {additionalElement && additionalElement}
      </Flex>
      <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
        <Input
          id={name}
          aria-label={field}
          value={value ?? ''}
          {...register(formField, {
            required: validations?.required
              ? labels.main.toLowerCase().includes('first') ||
                labels.main.toLowerCase().includes('last')
                ? `Please enter your ${labels.main}`
                : `${labels.main} is required`
              : false,
            maxLength: emailMaxLength
              ? {
                  value: emailMaxLength,
                  message: `${labels.main} must not exceed ${emailMaxLength} characters`
                }
              : undefined,
            validate: val => {
              if (validations?.required) {
                // If required, always validate
                return handleWhiteSpaceValidation(
                  val,
                  labels.main,
                  'minLength' in validations
                    ? validations.minLength
                    : undefined,
                  emailMaxLength
                );
              } else {
                // If not required, validate only if value is present
                if (val && val?.trim() !== '') {
                  return handleWhiteSpaceValidation(
                    val,
                    labels.main,
                    'minLength' in validations
                      ? validations.minLength
                      : undefined,
                    emailMaxLength
                  );
                }
                // If not required and value is empty, pass validation
                return true;
              }
            }
          })}
          isInvalid={!!error}
          placeholder={placeholder || labels.placeholder}
          onInput={e => {
            setValue(formField, e.currentTarget.value, {
              shouldValidate: true
            });
          }}
          isReadOnly={!isEditingAllowed}
          minLength={
            validations?.required && value && 'minLength' in validations
              ? validations.minLength
              : undefined
          }
          maxLength={emailMaxLength}
          autoComplete="off"
          onBlur={() => setValue(formField, value.trim() || '')}
          data-field-error={!!error}
        />

        {error?.message ? (
          <FormFeedback variant="error" data-error={`${fieldName}-error`}>
            {error.message}
          </FormFeedback>
        ) : (
          <FormFieldErrorMessage config={config} />
        )}
      </Flex>
    </Flex>
  );
};

export default CustomFormFieldTextInput;
